import { ChevronDown, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useLayoutDirection } from '../../hooks/useDirection.js';
import { cn } from '../../lib/cn.js';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Dim secondary line (e.g. a barcode). */
  sublabel?: string;
  /** Extra searchable text (code, barcode) — matched alongside the label. */
  keywords?: string;
  /**
   * Values that identify this option EXACTLY — barcodes, in practice. A query
   * equal to one of them hoists this option above every fuzzy match, so a
   * scanned label always beats a product whose CODE merely contains the same
   * digits. Without it the first row of the list wins, and at a counter that is
   * the wrong product in the cart.
   */
  exact?: string[];
}

export interface ComboboxProps {
  options: ComboboxOption[];
  /** Selected value; undefined when nothing is chosen. */
  value?: string | undefined;
  onChange: (value: string | undefined, option?: ComboboxOption) => void;
  placeholder?: string;
  ariaLabel?: string;
  id?: string;
  size?: 'sm' | 'md';
  /** Clear the text after a pick and keep focus — for a scan/add-another bar. */
  clearOnSelect?: boolean;
  /**
   * Fired when Enter is pressed with a query that matches NO loaded option — lets
   * a scan field resolve an unloaded barcode server-side. Receives the typed text.
   */
  onEnterUnmatched?: (query: string) => void;
  /**
   * Fired with every typed query, so the consumer can WIDEN `options` from the
   * server. The list this filters is whatever it was handed — a picker over a
   * catalogue of thousands only ever holds a page of it, and without this the
   * other 8,000 items simply cannot be typed for. Debounce on the consumer side.
   */
  onSearch?: (query: string) => void;
  autoFocus?: boolean;
  className?: string;
}

/**
 * A searchable single-select combobox: a text input that filters a list of
 * options by label + keywords (so items match on code/name/barcode), with
 * keyboard nav. Hardware barcode scanners type the code then Enter, which
 * picks the single match — so it doubles as a scan field. RTL- and
 * token-aware; mirrors the Input styling.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  ariaLabel,
  id,
  size = 'md',
  clearOnSelect = false,
  onEnterUnmatched,
  onSearch,
  autoFocus,
  className,
}: ComboboxProps): JSX.Element {
  const dir = useLayoutDirection();
  const selected = useMemo(() => options.find((o) => o.value === value), [options, value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  // The dropdown is rendered in a portal (so it escapes parent stacking
  // contexts like glass cards / table cells); track the anchor's viewport rect.
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 50);
    const hits = options.filter((o) => `${o.label} ${o.keywords ?? ''}`.toLowerCase().includes(q));
    // An EXACT identity match (a scanned barcode) leads, in the order it was
    // given; everything the fuzzy filter caught follows.
    const exact = options.filter((o) => o.exact?.some((v) => v.trim().toLowerCase() === q));
    if (exact.length === 0) return hits.slice(0, 50);
    const seen = new Set(exact.map((o) => o.value));
    return [...exact, ...hits.filter((o) => !seen.has(o.value))].slice(0, 50);
  }, [options, query]);

  // Close on outside click (ignore the portaled menu, which lives outside root).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      const inRoot = rootRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inRoot && !inMenu) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Position the portaled dropdown under the input; keep it in sync on
  // scroll/resize while open.
  useEffect(() => {
    if (!open) { setRect(null); return undefined; }
    const place = (): void => {
      const r = rootRef.current?.getBoundingClientRect();
      if (r) setRect({ left: r.left, top: r.bottom + 4, width: r.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  const choose = (opt: ComboboxOption): void => {
    onChange(opt.value, opt);
    setOpen(false);
    setQuery('');
    setActive(0);
    if (clearOnSelect) inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      // Enter belongs to the combobox, never to the form around it. It used to
      // fall through whenever nothing matched, and inside a <form> a
      // fall-through Enter IS the submit button: a scanner reading a code this
      // picker had not loaded saved the form it was sitting on.
      e.preventDefault();
      if (open && filtered.length > 0) {
        const opt = filtered[Math.min(active, filtered.length - 1)];
        if (opt) choose(opt);
      } else if (onEnterUnmatched && query.trim()) {
        // No loaded option matched — hand the raw text to the scan resolver.
        onEnterUnmatched(query.trim());
        setQuery('');
        setOpen(false);
        setActive(0);
        if (clearOnSelect) inputRef.current?.focus();
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    }
  };

  // While open, show the live query; while closed, show the selected label.
  const display = open ? query : selected?.label ?? '';
  const h = size === 'sm' ? 'h-9' : 'h-10';

  return (
    <div ref={rootRef} className="relative">
      <Search className="pointer-events-none absolute inset-y-0 start-2.5 my-auto h-4 w-4 text-fg-muted" aria-hidden />
      <input
        ref={inputRef}
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        autoComplete="off"
        autoFocus={autoFocus}
        className={cn(
          'block w-full rounded-md border border-border bg-bg ps-8 pe-8 text-sm text-fg outline-none transition',
          'placeholder:text-fg-muted focus:border-brand focus:ring-2 focus:ring-brand/20',
          h,
          className,
        )}
        placeholder={placeholder}
        value={display}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActive(0);
          onSearch?.(e.target.value);
        }}
        onFocus={(e) => {
          setOpen(true);
          setQuery(selected?.label ?? '');
          setActive(0);
          e.target.select();
        }}
        onKeyDown={onKeyDown}
      />
      {selected && !clearOnSelect ? (
        <button
          type="button"
          aria-label="Clear"
          onClick={() => {
            onChange(undefined);
            setQuery('');
            setOpen(false);
          }}
          className="absolute inset-y-0 end-2 my-auto grid h-5 w-5 place-items-center rounded text-fg-muted hover:text-fg"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : (
        <ChevronDown className="pointer-events-none absolute inset-y-0 end-2.5 my-auto h-4 w-4 text-fg-muted" aria-hidden />
      )}
      {open && filtered.length > 0 && rect && createPortal(
        // pointer-events-auto is NOT dead code: a modal (Radix) dialog sets
        // `pointer-events: none` on <body> while open, and this menu portals
        // into <body>. Without its own value the menu inherits `none`, every
        // mouse pick falls through to the dialog beneath, and the outside-click
        // handler closes the list unpicked. Pure hit-testing CSS — the
        // keyboard/scanner path never consults it.
        <ul
          ref={menuRef}
          dir={dir}
          className="glass-overlay pointer-events-auto fixed z-[60] max-h-64 overflow-auto rounded-md border py-1 shadow-lg"
          style={{ left: rect.left, top: rect.top, width: rect.width }}
          role="listbox"
        >
          {filtered.map((o, i) => (
            <li key={o.value} role="option" aria-selected={i === active}>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
                onMouseEnter={() => setActive(i)}
                className={cn(
                  'flex w-full flex-col items-start px-3 py-1.5 text-start text-sm',
                  i === active ? 'bg-bg-subtle' : 'hover:bg-bg-subtle',
                )}
              >
                <span className="font-medium text-fg">{o.label}</span>
                {o.sublabel && <span className="font-mono text-xs text-fg-muted">{o.sublabel}</span>}
              </button>
            </li>
          ))}
        </ul>,
        document.body,
      )}
    </div>
  );
}
